/*
 * usePromoLanding — detects ?promo=WELCOME10 / ?ref=<code> on ANY page
 * load, remembers it in sessionStorage so it survives navigation to the
 * order flow, and returns the code so the CartDrawer can pre-fill it.
 *
 * Two distinct code spaces:
 *   1. LANDING PROMOs (WELCOME10, MAMA5, FEAST15) — same as ?promo=X
 *   2. REFERRAL codes (MMxxxx) — friend's personal $5 share link;
 *      stacks on top of any explicit promo the user types.
 *
 * Behaviour:
 *   • First visit with ?promo=X → stored + toast
 *   • First visit with ?ref=MMxxxx → stored as referral + toast
 *   • Legacy ?ref=welcome|opening|sms → mapped to WELCOME10 promo
 *   • ?promo=clear / ?ref=clear → clears BOTH stores
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "mm_landing_promo";
const REFERRAL_KEY = "mm_referral_code";
const TOAST_FLAG = "mm_landing_promo_toasted";
const REF_TOAST_FLAG = "mm_referral_toasted";

// Legacy short-link remaps — keep for SMS campaigns already in the wild.
const REF_TO_PROMO = {
  welcome: "WELCOME10",
  opening: "WELCOME10",
  sms: "WELCOME10",
};

// A real referral code from `/api/referrals/share` is MM + 4 alphanumerics.
const REFERRAL_PATTERN = /^MM[A-Z0-9]{4}$/;

export function usePromoLanding(toastFn) {
  const [promoCode, setPromoCode] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || ""; }
    catch { return ""; }
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = (params.get("promo") || "").trim().toUpperCase();
      const ref = (params.get("ref") || "").trim();
      const refUpper = ref.toUpperCase();
      const refLower = ref.toLowerCase();
      const clear = raw === "CLEAR" || refLower === "clear";

      if (clear) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(REFERRAL_KEY);
        sessionStorage.removeItem(TOAST_FLAG);
        sessionStorage.removeItem(REF_TOAST_FLAG);
        setPromoCode("");
        return;
      }

      // Real referral code — persist separately in localStorage so it
      // survives across tabs and until checkout.
      if (REFERRAL_PATTERN.test(refUpper)) {
        localStorage.setItem(REFERRAL_KEY, refUpper);
        if (!sessionStorage.getItem(REF_TOAST_FLAG) && toastFn) {
          sessionStorage.setItem(REF_TOAST_FLAG, "1");
          toastFn({
            title: "A friend sent you $5 off",
            description: "Your discount will apply automatically at checkout.",
          });
        }
      }

      // Landing promo remap (legacy `ref=welcome`) OR explicit `?promo=X`
      const resolved = raw || REF_TO_PROMO[refLower] || "";
      if (resolved && resolved !== promoCode) {
        sessionStorage.setItem(STORAGE_KEY, resolved);
        setPromoCode(resolved);
        if (!sessionStorage.getItem(TOAST_FLAG) && toastFn) {
          sessionStorage.setItem(TOAST_FLAG, "1");
          toastFn({
            title: `Discount unlocked — ${resolved}`,
            description: "10% off your first order will apply automatically at checkout.",
          });
        }
      }
    } catch (err) {
      console.warn("[promo-landing] failed to read URL params", err);
    }
  }, [promoCode, toastFn]);

  return promoCode;
}

export function clearLandingPromo() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(TOAST_FLAG);
  } catch (err) {
    console.warn("[promo-landing] failed to clear", err);
  }
}

/** Returns the persisted referral code (MMxxxx) or empty string. */
export function getReferralCode() {
  try {
    return localStorage.getItem(REFERRAL_KEY) || "";
  } catch {
    return "";
  }
}

/** Clear after a completed order so the friend doesn't accidentally re-use. */
export function clearReferralCode() {
  try {
    localStorage.removeItem(REFERRAL_KEY);
  } catch {
    // ignore
  }
}
